/**
 * TASK 41 — Matches tab inside JobDetailPage.
 * Shows the top candidates for a specific job, sorted by composite_score.
 */
import { Loader2, XCircle, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { ConfirmDialog } from '@shared/components/ui/ConfirmDialog';
import { useQueryClient } from '@tanstack/react-query';
import { useMatchResults } from '@modules/smart-match/hooks/useMatchResults';
import { useAuth } from '@core/auth/useAuth';
import { matchResultService } from '@core/supabase/services/matchResultService';
import { queryKeys } from '@core/query/queryKeys';
import { SmartMatchResultCard } from '@modules/smart-match/components/SmartMatchResultCard';
import { bandCountsFor } from '@modules/smart-match/lib/matchDisplay';
import { useToast } from '@shared/hooks/useToast';

interface Props {
  jobId: string;
}

export function JobMatchesTab({ jobId }: Props) {
  const { user } = useAuth();
  const orgId = user?.orgId ?? '';
  const queryClient = useQueryClient();
  const { data: matches = [], isLoading } = useMatchResults({ jobId });
  const [clearing, setClearing] = useState(false);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const { toast, showToast } = useToast();

  const counts = bandCountsFor(matches);
  const preservedN = matches.filter((m) => m.is_preserved).length;

  const runClearThisJob = async () => {
    if (!orgId) return;
    setClearing(true);
    try {
      await matchResultService.clearUnpreservedForJob(orgId, jobId);
      void queryClient.invalidateQueries({ queryKey: queryKeys.smartMatch.matchResultsByJob(orgId, jobId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.smartMatch.matchResults(orgId) });
      void queryClient.invalidateQueries({ queryKey: ['smart-match'] });
    } catch (e) {
      showToast({ title: e instanceof Error ? e.message : 'Clear failed.', kind: 'error' });
    } finally {
      setClearing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center gap-4 py-20">
        <Loader2 className="h-10 w-10 animate-spin text-teal-500" />
        <p className="text-sm text-slate-500">Loading top candidates…</p>
      </div>
    );
  }

  if (matches.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-slate-200 bg-white py-16 dark:border-slate-700 dark:bg-slate-900">
        <XCircle className="h-8 w-8 text-slate-300 dark:text-slate-600" />
        <p className="text-sm text-slate-500">
          No candidates matched against this position yet. Upload resumes in Smart Match to generate recommendations.
        </p>
      </div>
    );
  }

  const unpreservedN = matches.filter((m) => !m.is_preserved).length;

  return (
    <div>
      <ConfirmDialog
        open={clearConfirmOpen}
        onClose={() => setClearConfirmOpen(false)}
        onConfirm={() => {
          setClearConfirmOpen(false);
          void runClearThisJob();
        }}
        title="Clear matches?"
        message={`Remove ${unpreservedN} unpreserved match row(s) for this job only? Preserved snapshots stay.`}
        confirmLabel="Clear"
        variant="danger"
        requireTypedConfirm="DELETE"
      />
      {toast}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-slate-700 dark:text-slate-200">
            {matches.length} candidate{matches.length !== 1 ? 's' : ''} ranked by match score
          </p>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Strong {counts.strong_fit} · Good {counts.good_fit} · Low {counts.partial_fit} · Not recommended{' '}
            {counts.not_recommended} · {preservedN} preserved
          </p>
        </div>
        {orgId && (
          <button
            type="button"
            disabled={clearing}
            onClick={() => setClearConfirmOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-100 disabled:opacity-50 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300"
          >
            <Trash2 className="h-3.5 w-3.5" />
            {clearing ? 'Clearing…' : 'Clear unpreserved for this job'}
          </button>
        )}
      </div>
      <div className="ggj-match-cards-grid grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2 sm:gap-x-7 sm:gap-y-9 xl:grid-cols-3">
        {matches.map((m) => (
          <SmartMatchResultCard key={`${m.candidate_id}-${m.job_id}`} match={m} variant="byJob" />
        ))}
      </div>
    </div>
  );
}
